import { Link } from 'react-router-dom';
import { ArrowRight, Check, Globe, Smartphone, Zap, Search, Shield, RefreshCw } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const fordeler = [
  {
    icon: Smartphone,
    title: 'Mobilvennlig design',
    description: 'Alle våre nettsider er optimalisert for mobil, nettbrett og desktop.',
  },
  {
    icon: Zap,
    title: 'Rask lastetid',
    description: 'Optimalisert kode og bilder for best mulig ytelse og brukeropplevelse.',
  },
  {
    icon: Search,
    title: 'SEO-optimalisert',
    description: 'Bygget fra bunnen av med fokus på synlighet i søkemotorer.',
  },
  {
    icon: Shield,
    title: 'Sikkerhet',
    description: 'SSL-sertifikat og sikre løsninger som standard.',
  },
  {
    icon: RefreshCw,
    title: 'Enkel oppdatering',
    description: 'Brukervennlige løsninger som lar deg enkelt oppdatere innhold selv.',
  },
  {
    icon: Globe,
    title: 'Skreddersydd',
    description: 'Unik design tilpasset din merkevare og dine behov.',
  },
];

const prosess = [
  { step: '01', title: 'Oppstartsmøte', description: 'Vi kartlegger dine behov, mål og ønsker for nettsiden.' },
  { step: '02', title: 'Design', description: 'Vi lager et designforslag basert på din merkevare.' },
  { step: '03', title: 'Utvikling', description: 'Vi bygger nettsiden med fokus på ytelse og brukervennlighet.' },
  { step: '04', title: 'Testing', description: 'Grundig testing på alle enheter før lansering.' },
  { step: '05', title: 'Lansering', description: 'Vi lanserer nettsiden og sørger for at alt fungerer.' },
  { step: '06', title: 'Oppfølging', description: 'Løpende support og vedlikehold etter lansering.' },
];

export default function Nettside() {
  return (
    <div>
      <PageSEO title="Ny nettside – Profesjonell webutvikling i hele Norge" path="/tjenester/nettside" description="Vi bygger profesjonelle og raske nettsider for bedrifter over hele Norge. Mobilvennlig design, SEO-optimalisert og skreddersydd for din vekst." />
      {/* Hero */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="nettside-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Tjeneste
              </p>
              <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
                Ny nettside som gir resultater
              </h1>
              <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
                Vi utvikler profesjonelle nettsider som ikke bare ser bra ut, men som faktisk konverterer besøkende til kunder. Mobilvennlig, rask og optimalisert for søkemotorer.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group mt-8"
                data-testid="nettside-cta"
              >
                Start ditt prosjekt
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="/premium-web-showcase.png"
                alt="Webutvikling – Side Kraft leverer nettsider i hele Norge"
                className="relative z-10 w-full h-[450px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Fordeler */}
      <section className="py-24 md:py-32" data-testid="nettside-fordeler">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Fordeler
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Hva du får med oss
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {fordeler.map((fordel, index) => (
              <div
                key={index}
                className="p-8 border border-border rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`fordel-${index}`}
              >
                <fordel.icon className="w-10 h-10 text-accent mb-6" />
                <h3 className="font-syne font-bold text-xl text-primary mb-3">
                  {fordel.title}
                </h3>
                <p className="font-manrope text-sm text-muted-foreground leading-relaxed">
                  {fordel.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prosess */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="nettside-prosess">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Vår prosess
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight">
              Fra idé til ferdig nettside
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {prosess.map((item, index) => (
              <div
                key={index}
                className="relative p-8 border border-primary-foreground/10 rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`prosess-${index}`}
              >
                <span className="font-syne font-bold text-5xl text-accent/20 absolute top-4 right-4">
                  {item.step}
                </span>
                <h3 className="font-syne font-bold text-lg mb-3 relative z-10">
                  {item.title}
                </h3>
                <p className="font-manrope text-sm text-primary-foreground/70 leading-relaxed relative z-10">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-muted" data-testid="nettside-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Klar for en ny nettside?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Ta kontakt for en uforpliktende samtale om hvordan vi kan hjelpe deg med en nettside som gir resultater.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="nettside-bottom-cta"
          >
            Kontakt oss
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
